import { API, graphqlOperation } from 'aws-amplify';
import { produce } from 'immer';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  onDashboard,
  onDashboardDeleted,
  onDocSendIngestionCreation
} from '../../../graphql/subscriptions';
import {
  getGroupDashboardQueriesFunc,
  getGroupDashboardsFunc,
  updateInvestmentThesisFunc
} from '../../../lib/helper';
import queryClient from '../../../services/queryClient';
import {
  Dashboard,
  DashboardDeckCreation,
  InvestmentThesis,
  Workspace
} from '../../../types/files';
import ArrayUtils from '../../../utils/ArrayUtils';
import ObjectUtils from '../../../utils/ObjectUtils';
import useBackwardsCompatible from '../../useBackwardsCompatible';
import useDashboardsContext from '../useDashboardsContext';
import useWorkspacesContext from '../useWorkspacesContext';

const useDashboardsEffects = (userGroup: string | null) => {
  const { dashboards, setDashboards, setDashboardsQueries, setDashboardsLoaded } = useDashboardsContext();
  const { workspaces, setWorkspaces } = useWorkspacesContext();
  const { dashboardWorkspaces } = useBackwardsCompatible();

  const [dashboardDecksInProgress, setDashboardDecksInProgress] = useState<DashboardDeckCreation[]>([]);
  const dashboardSubscriptionRef = useRef<any>(null);
  const dashboardDeletedSubscriptionRef = useRef<any>(null);
  const dashboardDeckCreationSubscriptionRef = useRef<any>(null);
  const dashboardDeckCreationFinishedSubscriptionRef = useRef<any>(null);

  const updateSubscribedDashboards = useCallback((localDashboards: Dashboard[], dashboard?: Dashboard|null, remove?: boolean) => {
    if (!dashboard?.id)
      return;

    let newDashboards = [];

    if (remove) {
      newDashboards = localDashboards.filter(d => d.id !== dashboard?.id);
    } else if (!!localDashboards.find(d => d.id === dashboard?.id)) {
      newDashboards = localDashboards.map(d => d.id !== dashboard?.id ? d : dashboard);
    } else {
      newDashboards = [dashboard, ...localDashboards];
    }

    setDashboards(ArrayUtils.sortByDescending(newDashboards, 'createdAt'));
  // eslint-disable-next-line
  }, []);

  const subscribeDashboards = useCallback(async (localDashboards: Dashboard[]) => {
    if (dashboardSubscriptionRef.current)
      dashboardSubscriptionRef.current.unsubscribe();

    if (userGroup) {
      dashboardSubscriptionRef.current = await (API.graphql(
        graphqlOperation(onDashboard, { group: userGroup })) as any)
          .subscribe({ next: ({ value }: any) =>
            updateSubscribedDashboards(localDashboards, value?.data?.onDashboard)});
    }
  }, [userGroup, updateSubscribedDashboards]);

  const subscribeDashboardDeleted = useCallback(async (localDashboards: Dashboard[]) => {
    if (dashboardDeletedSubscriptionRef.current)
      dashboardDeletedSubscriptionRef.current.unsubscribe();

    if (userGroup) {
      dashboardDeletedSubscriptionRef.current = await (API.graphql(
        graphqlOperation(onDashboardDeleted, { group: userGroup })) as any)
          .subscribe({ next: ({ value }: any) =>
            updateSubscribedDashboards(localDashboards, value?.data?.onDashboardDeleted, true)});
    }
  }, [userGroup, updateSubscribedDashboards]);

  const subscribeDashboardDeckCreateFinished = useCallback(async (localDashboardDecks: DashboardDeckCreation[]) => {
    if (dashboardDeckCreationFinishedSubscriptionRef.current)
      dashboardDeckCreationFinishedSubscriptionRef.current.unsubscribe();

    if (userGroup) {
      dashboardDeckCreationFinishedSubscriptionRef.current = await (API.graphql(
        graphqlOperation(onDashboard, { group: userGroup })) as any)
          .subscribe({ next: async ({ value }: any) => {
            if (!value?.data?.onDashboard?.id)
              return;

            if (!!localDashboardDecks?.length)
              setDashboardDecksInProgress(localDashboardDecks.filter(deck => deck.newDashboardTitle !== value.data.onDashboard.title));
          }});
    }
  }, [userGroup]);

  const subscribeDashboardDeckCreated = useCallback(async (localDashboardDecks: DashboardDeckCreation[]) => {
    if (dashboardDeckCreationSubscriptionRef.current)
      dashboardDeckCreationSubscriptionRef.current.unsubscribe();

    if (userGroup) {
      dashboardDeckCreationSubscriptionRef.current = await (API.graphql(
        graphqlOperation(onDocSendIngestionCreation, { group: userGroup })) as any)
          .subscribe({ next: async ({ value }: any) => {
            if (!value?.data?.onDocSendIngestionCreation?.newDashboardTitle)
              return;

            setDashboardDecksInProgress([{ ...value.data.onDocSendIngestionCreation }, ...localDashboardDecks]);
          }});
    }
  }, [userGroup]);

  useEffect(() => {
    subscribeDashboards(dashboards);
    subscribeDashboardDeleted(dashboards);

    return () => {
      dashboardSubscriptionRef?.current?.unsubscribe();
      dashboardDeletedSubscriptionRef?.current?.unsubscribe();
    }
  }, [dashboards, subscribeDashboards, subscribeDashboardDeleted]);

  useEffect(() => {
    subscribeDashboardDeckCreated(dashboardDecksInProgress);
    subscribeDashboardDeckCreateFinished(dashboardDecksInProgress);

    return () => {
      dashboardDeckCreationSubscriptionRef?.current?.unsubscribe();
      dashboardDeckCreationFinishedSubscriptionRef?.current?.unsubscribe();
    }
  }, [dashboardDecksInProgress, subscribeDashboardDeckCreated, subscribeDashboardDeckCreateFinished]);

  useEffect(() => {
    if (userGroup && !dashboardSubscriptionRef.current) {
      setDashboardsLoaded(false);
      getGroupDashboardsFunc(userGroup).then((dashboardsData) => {
        setDashboards(dashboardsData);
        setDashboardsLoaded(true);
      });
      getGroupDashboardQueriesFunc(userGroup).then((bulkQueryData) => {
        setDashboardsQueries(prev => produce(prev, draft => {
          bulkQueryData.forEach(newQuery => {
            if (!draft.find(query => query.dashboardId === newQuery.dashboardId && query.title === newQuery.title))
              draft.push(newQuery);
          });
        }));
      });
    }
  // eslint-disable-next-line
  }, [userGroup]);

  useEffect(() => {
    if (userGroup && !!workspaces.length && !!dashboards.length) {
      workspaces.filter(workspace => !workspace.isShared).forEach(workspace => {
        const assignedDashboards = dashboards.filter(dashboard => dashboardWorkspaces(dashboard)?.includes(workspace.id));
        const tagCounts = assignedDashboards.map(dashboard => dashboard.tags)
          .filter(tags => !!tags?.length).flat()
          .reduce((acc: Record<string, number>, tag) => {
            if (!acc[tag!])
              acc[tag!] = 1;
            else
              acc[tag!]++;

            return acc;
          }, {});
        const stageCounts = assignedDashboards.map(dashboard => dashboard.investmentStage)
          .filter(stages => !!stages?.length).flat()
          .reduce((acc: Record<string, number>, stage) => {
            if (!acc[stage!])
              acc[stage!] = 1;
            else
              acc[stage!]++;

            return acc;
        }, {});
        const updateTags = Array.from(new Set([
          ...workspace.investmentThesis?.focusArea?.tags ?? [],
          ...Object.keys(tagCounts).filter(tag => tagCounts[tag] >= 2)]
        ));
        const updateStages = Array.from(new Set([
          ...workspace.investmentThesis?.stage?.stages ?? [],
          ...Object.keys(tagCounts).filter(stage => stageCounts[stage] >= 2)]
        ));

        if (!ObjectUtils.equalObjects(workspace.investmentThesis?.focusArea?.tags, updateTags) ||
          !ObjectUtils.equalObjects(workspace.investmentThesis?.stage?.stages, updateStages)) {
          const investmentThesis: InvestmentThesis = {
            ...workspace.investmentThesis,
            focusArea: { tags: updateTags },
            stage: { stages: updateStages },
          };

          updateInvestmentThesisFunc({
            id: workspace.id,
            group: userGroup!,
            investmentThesis: investmentThesis,
          }).then((updatedWorkspace?: Workspace) => {
            setWorkspaces(prev => prev.map(ws =>
                ws.id === workspace.id ? {...updatedWorkspace!} : ws
            ));
            queryClient.refetchQueries({queryKey: ['getGroupSettings', '']});
          });
        }
      });
    }
  // eslint-disable-next-line
  }, [dashboards]);

  return null;
};

export default useDashboardsEffects;